import React, { FC, useMemo, useState, useRef, useEffect, useId } from "react";
import Box from "../../Unknown/Box";
import Autocomplete from "../../Unknown/Autocomplete";
import CircularProgress from "../../Unknown/CircularProgress";
import Grid from "../../Unknown/Grid";
import ListSubheader from "../../Unknown/ListSubheader";
import TextField from "../../Unknown/TextField";
import Typography from "../../Unknown/Typography";
import useStyles from "./useStyles";

export type Option = {
  groupId: string;
  value: string;
  label: string;
  price: string;
  description: string;
  discountPrice?: string;
  isDisabled?: boolean;
  metadata?: Record<string, string | boolean | number | null>;
};

export type Groups = Record<string, string>;

interface SelectCenterServiceInputProps {
  inputLabel: string;
  options: Option[];
  groups: Groups;
  onChange: (value: Option) => void;
  isDiscountShown?: boolean;
  selectedOption?: Option | null;
  isLoading?: boolean;
  isDisabled?: boolean;
}

const SelectCenterServiceInput: FC<SelectCenterServiceInputProps> = ({
  inputLabel,
  options,
  groups,
  isDiscountShown,
  selectedOption,
  onChange,
  isLoading,
  isDisabled,
}) => {
  const { groupRoot, oldPriceTextRoot } = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteId = useId();

  useEffect(() => {
    const isMobile = window.innerWidth < 768;

    if (!isOpen || !containerRef.current || !isMobile) {
      return undefined;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const elementTop = rect.top;
    const targetScrollPosition = window.pageYOffset + elementTop - 20;
    const maxScrollPosition =
      document.documentElement.scrollHeight - window.innerHeight;

    // Check if we need extra padding to scroll the element to the top
    const needsExtraPadding = targetScrollPosition > maxScrollPosition;
    let addedPadding = false;

    if (needsExtraPadding) {
      document.body.style.paddingBottom = "50vh";
      addedPadding = true;
    }

    setTimeout(() => {
      const updatedRect = containerRef.current?.getBoundingClientRect();
      const updatedElementTop = updatedRect?.top || 0;
      const scrollPosition = window.pageYOffset + updatedElementTop - 20;
      window.scrollTo({ top: scrollPosition, behavior: "smooth" });
    }, 100);

    return () => {
      if (addedPadding) {
        document.body.style.paddingBottom = "";
      }
    };
  }, [isOpen]);

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => {
      const groupCompare = groups[a.groupId]?.localeCompare(
        groups[b.groupId] || "",
      );
      if (groupCompare !== 0) return groupCompare;
      return a.label.localeCompare(b.label);
    });
  }, [options, groups]);

  return (
    <Box ref={containerRef}>
      <Autocomplete
        fullWidth
        id={autocompleteId}
        options={sortedOptions}
        value={selectedOption ?? undefined}
        disabled={isDisabled}
        loading={isLoading}
        open={isOpen}
        disableClearable
        onOpen={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        onClose={() => {
          setIsOpen(false);
          setInputValue("");
        }}
        inputValue={inputValue}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            setInputValue(newInputValue);
          } else if (reason === "reset" && !isOpen) {
            setInputValue("");
          }
        }}
        onChange={(event, value) => {
          if (value) {
            onChange(value);
            setInputValue("");
          }
        }}
        getOptionLabel={(option) => option.label}
        getOptionDisabled={(option) => option.isDisabled || false}
        groupBy={(option) => groups[option.groupId] || ""}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        filterOptions={(opts, state) => {
          const searchValue = state.inputValue.toLowerCase();
          if (!searchValue) return opts;
          return opts.filter(
            (option) =>
              option.label.toLowerCase().includes(searchValue) ||
              option.description?.toLowerCase().includes(searchValue),
          );
        }}
        ListboxProps={{
          style: {
            maxHeight: "min(350px, 85vh)",
            padding: 0,
          },
        }}
        slotProps={{
          paper: {
            elevation: 8,
          },
        }}
        renderInput={(params) => {
          const hasValue = selectedOption && !isOpen;

          return (
            <Box
              sx={{ position: "relative" }}
              onClick={() => {
                if (!isDisabled) {
                  setIsOpen(true);
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 0);
                }
              }}
            >
              {/* Always render TextField for MUI internal refs */}
              <Box
                sx={{
                  ...(hasValue && {
                    position: "absolute",
                    opacity: 0,
                    pointerEvents: "none",
                    width: "100%",
                  }),
                }}
              >
                <TextField
                  {...params}
                  label={inputLabel}
                  required
                  inputRef={inputRef}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              </Box>
              {/* Custom display when value is selected */}
              {hasValue && (
                <Box
                  sx={{
                    border: "1px solid rgba(0, 0, 0, 0.23)",
                    borderRadius: 1,
                    padding: "16.5px 65px 16.5px 14px",
                    cursor: isDisabled ? "default" : "pointer",
                    minHeight: "56px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                    "&:hover": {
                      borderColor: isDisabled
                        ? "rgba(0, 0, 0, 0.23)"
                        : "rgba(0, 0, 0, 0.87)",
                    },
                    opacity: isDisabled ? 0.6 : 1,
                    backgroundColor: "transparent",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      position: "absolute",
                      top: "-9px",
                      left: "12px",
                      backgroundColor: "background.paper",
                      px: 0.5,
                      color: "text.secondary",
                      fontSize: "0.75rem",
                    }}
                  >
                    {inputLabel}
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                    {selectedOption.label}
                  </Typography>
                  {selectedOption.description && (
                    <Typography
                      variant="body2"
                      color="text.primary"
                      lineHeight={2}
                      letterSpacing={0.4}
                      sx={{ wordBreak: "break-word", mt: 0.5 }}
                    >
                      {selectedOption.description}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      position: "absolute",
                      right: "9px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {isLoading && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {params.InputProps.endAdornment}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          );
        }}
        renderGroup={(params) => (
          <Box key={params.key}>
            <ListSubheader
              className={groupRoot}
              sx={{
                position: "sticky",
                top: 0,
                mt: 2,
                zIndex: 10,
                lineHeight: "unset",
              }}
            >
              <Box py={2}>
                <Typography
                  variant="h3"
                  color="common.black"
                  sx={{ wordBreak: "break-word" }}
                >
                  {params.group}
                </Typography>
              </Box>
            </ListSubheader>
            {params.children}
          </Box>
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.value}>
            <Grid container direction="row" flexWrap="nowrap" spacing={2}>
              <Grid item container flex={1} direction="column">
                <Grid item>
                  <Typography
                    variant="body1"
                    whiteSpace="normal"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {option.label}
                  </Typography>
                </Grid>
                {option.description && (
                  <Grid item>
                    <Typography
                      variant="caption"
                      whiteSpace="normal"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {option.description}
                    </Typography>
                  </Grid>
                )}
              </Grid>
              <Grid
                item
                container
                width="fit-content"
                direction="column"
                alignItems="flex-end"
                justifyContent="center"
              >
                {isDiscountShown && option.discountPrice ? (
                  <>
                    <Grid item>
                      <Typography className={oldPriceTextRoot}>
                        {option.price}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography>{option.discountPrice}</Typography>
                    </Grid>
                  </>
                ) : (
                  <Grid item>
                    <Typography>{option.price}</Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      />
    </Box>
  );
};

export default SelectCenterServiceInput;
